import { HttpClient } from '@angular/common/http';
import {
  Component,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import moment from 'moment-timezone/moment-timezone';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import { Dates } from '@inmoment/the-kitchen';

import { Constants } from 'app/constants/constants';
import { FilterService, WidgetQueryOptions } from 'app/modules/collection/services/filter.service';
import { DashboardViewComponent } from 'app/modules/dashboard/pages/dashboard-view/dashboard-view.component';
import { CustomFieldsService } from 'app/modules/field/custom-fields.service';
import { AuthService } from 'app/providers/bridge-api/auth.service';
import { StorageAccountService } from 'app/providers/storage-api/blender-account.service';
import { FieldService } from 'app/providers/storage-api/field.service';
import { InteractiveSmartSummaryService } from 'app/providers/storage-api/interactive-smart-summary.service';
import {
  Calculation,
  PredictiveService,
  ResultColumn,
} from 'app/providers/storage-api/predictive.service';
import { AlertService } from 'app/services/alert.service';
import { DialogService } from 'app/services/dialog.service';
import {
  injectComponentTelemetryService,
  EnableComponentTelemetry,
  EnableClassTelemetry,
} from 'app/services/otel.service';
import { ResponsiveService } from 'app/services/responsive.service';
import { SystemService } from 'app/services/system.service';
import { Utils } from 'app/utils/utils';
import { WidgetDataSource } from '../../../../models';
import { QueryParams } from '../../../../providers/api.service';
import {
  Comparison,
  WidgetDefault,
  WidgetGroup,
} from '../../../../providers/bridge-api/dashboard.service';
import { TimezoneService } from '../../../../services/timezone.service';
import { MetricSettingsModalResult } from '../metric-settings-modal/metric-settings-modal.component';
import { PredictiveBreakdownWidgetetteComponent } from '../predictive-breakdown-widgetette/predictive-breakdown-widgetette.component';
import { PredictiveChartWidgetetteComponent } from '../predictive-chart-widgetette/predictive-chart-widgetette.component';
import { PredictiveCrosstabComponent } from '../predictive-crosstab/predictive-crosstab.component';
import { PredictiveDateRange } from '../predictive-widget-datepickers/predictive-widget-datepickers.component';
import { SmartSummaryDialogComponent } from '../smart-summary-dialog/smart-summary-dialog.component';
import { AbstractWidgetComponent } from '../widget/abstract-widget.component';
import { WidgetComponent } from '../widget/widget.component';
import { Breakdown, Driver } from '../../models/predictive.models';
import { ActionPlanIpdService } from '../../services/action-plan-ipd.service';
import { PptxReport } from '../../services/pptx.service';
import { PredictiveWidgetDataService } from '../../services/predictive-widget-data.service';
import { WidgetCommentsHandlerService } from '../../services/widget-comments-handler.service';
import { PredictiveSimulationService } from './predictive-simulation.service';
import { createRangeFromMetricSettings } from './predictive.utils';
import { SimulationStorageService } from './simulation-storage.service';

export type PredictorView = 'crosstab' | 'predictor';
export type ScoringStrategy = 'AVERAGE' | 'BOX' | 'NPS';
export type BoxSize = '1' | '2' | '3' | '4' | '5' | '6' | '7';
export type BoxDirection = 'BOTTOM' | 'TOP';
export type StepValue = '1%' | '5%' | '10%';
export type PlotType = 'Average' | 'Median';
export type Decimals = '0' | '1' | '2';
export type MetricScaleMin = '0' | '1';

export interface MetricSettings {
  scoring: ScoringStrategy;
  boxSize?: BoxSize;
  boxDirection?: BoxDirection;
  min: MetricScaleMin;
  max: number;
}

export interface WidgetPredictiveOptions extends WidgetDefault {
  simulations: Simulation[];
  stepValue: StepValue;
  decimals: Decimals;
  view: PredictorView;
  scatterValue: PlotType;
  displayLabels: boolean;
  mainSettings: MetricSettings;
  driverSettings: MetricSettings;
  excludedValues?: {
    name: string;
    displayName?: string;
  }[];
}

export interface PredictiveChartOptionParams {
  dependentRange?: string;
  driversRange?: string;
  scoringStrategy: ScoringStrategy;
  boxSize?: BoxSize;
  boxDirection?: BoxDirection;
  minDate?: string;
  maxDate?: string;
  query?: string;
  timezone?: string;
  excludedBreakdowns?: string[];
}

export interface Simulation {
  breakdown: string;
  drivers: SimulatedValue[];
}

export interface SimulatedValue {
  driver: string;
  value: number;
}

@EnableClassTelemetry()
@Component({
  selector: 'app-widget-predictive',
  templateUrl: './widget-predictive.component.html',
  styleUrls: ['./widget-predictive.component.scss'],
  providers: [PredictiveSimulationService, SimulationStorageService],
  standalone: false,
})
export class WidgetPredictiveComponent
  extends AbstractWidgetComponent<WidgetPredictiveOptions>
  implements OnInit, OnChanges, OnDestroy
{
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  @Input() public widgetDataSource: WidgetDataSource;
  @Input() public dateRange: PredictiveDateRange;

  @ViewChild(PredictiveChartWidgetetteComponent) chartComponent: PredictiveChartWidgetetteComponent;
  @ViewChild(PredictiveBreakdownWidgetetteComponent)
  breakdownComponent: PredictiveBreakdownWidgetetteComponent;
  @ViewChild(PredictiveCrosstabComponent)
  crosstabComponent: PredictiveCrosstabComponent;

  public downloadFormats = ['csv', 'pptx'];

  canFilter = false;
  public hasAnalyses = true;

  breakdownCalculation: Calculation = null;
  driverCalculation: Calculation = null;
  trendCalculation: Calculation = null;
  driverOptionsButtonText: string;
  breakdownOptionsButtonText: string;
  breakdownTitle: string;
  displayedTrendDateRange: Dates;
  displayedCurrentDateRange: Dates;

  public dataSourceNotFoundText = '';
  twtraAsAWidget = false;

  canCalculateTrend: boolean;

  selectedBreakdown: Breakdown;
  breakdowns: Breakdown[];
  allBreakdown: Breakdown;
  subBreakdowns: Breakdown[];

  hoveredDriver: Driver | null;
  isPanelOpen: boolean;

  private readonly responsiveService = inject(ResponsiveService);
  private readonly simulationService = inject(PredictiveSimulationService);
  private readonly simulationStorageService = inject(SimulationStorageService);

  private unsub$ = new Subject<void>();

  constructor(
    private dashboardViewComponent: DashboardViewComponent,
    private predictiveService: PredictiveService,
    private predictiveWidgetDataService: PredictiveWidgetDataService,
    private actionPlanIpdService: ActionPlanIpdService,
    private translocoService: TranslocoService,
    private httpClient: HttpClient,
    protected systemService: SystemService,
    protected accountService: StorageAccountService,
    protected authService: AuthService,
    protected timezoneService: TimezoneService,
    protected filterService: FilterService,
    protected alertService: AlertService,
    protected interactiveSmartSummaryService: InteractiveSmartSummaryService,
    protected widgetComponent: WidgetComponent,
    protected customFieldService: CustomFieldsService,
    protected commentsHandlerService: WidgetCommentsHandlerService,
    private dialogService: DialogService
  ) {
    super(
      systemService,
      accountService,
      authService,
      timezoneService,
      filterService,
      alertService,
      interactiveSmartSummaryService,
      widgetComponent,
      customFieldService,
      commentsHandlerService
    );
    this.canBeCloned = false;
  }

  async ngOnInit() {
    super.ngOnInit();

    this.simulationStorageService.init(this.payload.simulations);

    this.responsiveService
      .getIsSidePanelOpen()
      .pipe(takeUntil(this.unsub$))
      .subscribe((isOpen) => {
        this.isPanelOpen = isOpen;
      });

    this.twtraAsAWidget = this.dashboard.payload.type !== 'predictive';
    this.canBeDeleted = this.twtraAsAWidget;
    this.translocoService
      .selectTranslate('widgets.kda-deleted-select-another')
      .pipe(takeUntil(this.unsub$))
      .subscribe((translation) => (this.dataSourceNotFoundText = translation));

    this.setScoringMethodInWidgetGroup();

    this.convertFilterPeriodsToIso();

    this.notifyScoringStrategyChange();

    await this.refresh(false, true);

    if (this.isSmartSummaryEnabled) {
      this.interactiveSmartSummaryMenu = this.buildSmartSummaryMenu();
      this.removeInteractiveSmartSummaryMenu = this.buildSmartSummaryRemoveMenu();
    }
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.actionPlanIpdService.reset();

    this.unsub$.next();
    this.unsub$.complete();
  }

  public async refresh(
    reset = true,
    initialLoading = false,
    refreshedDatasource?: WidgetDataSource,
    isDataSourceChanged = false
  ): Promise<void> {
    this.loadingStarted.emit(initialLoading);
    this.isLoading = true;

    if (this.hasInvalidComparison) {
      this.loadingFinished.emit(initialLoading);
      this.isLoading = false;

      return;
    }

    if (this.widgetDataSource) {
      this.dataSourceNotFound = false;

      const breakdownCalculationParams: PredictiveChartOptionParams = {
        dependentRange: createRangeFromMetricSettings(this.payload.mainSettings),
        scoringStrategy: this.payload.mainSettings.scoring,
        boxDirection: this.payload.mainSettings.boxDirection,
        boxSize: this.payload.mainSettings.boxSize,
        timezone: this.timezoneService.timezone,
        excludedBreakdowns: this.payload.excludedValues?.map((value) => value.name) ?? [],
      };

      const driverCalculationParams: PredictiveChartOptionParams = {
        driversRange: createRangeFromMetricSettings(this.payload.driverSettings),
        scoringStrategy: this.payload.driverSettings.scoring,
        boxDirection: this.payload.driverSettings.boxDirection,
        boxSize: this.payload.driverSettings.boxSize,
        timezone: this.timezoneService.timezone,
        excludedBreakdowns: this.payload.excludedValues?.map((value) => value.name) ?? [],
      };

      this.twtraAsAWidget
        ? await this.refreshNew(
            breakdownCalculationParams,
            driverCalculationParams,
            reset,
            initialLoading,
            refreshedDatasource,
            isDataSourceChanged
          )
        : //For old templated twtra widgets
          await this.refreshOld(
            breakdownCalculationParams,
            driverCalculationParams,
            reset,
            initialLoading,
            refreshedDatasource,
            isDataSourceChanged
          );
      this.breakdownOptionsButtonText = this.setModalButtonText(
        breakdownCalculationParams,
        'breakdown'
      );
      this.driverOptionsButtonText = this.setModalButtonText(driverCalculationParams, 'driver');
    } else {
      this.dataSourceNotFound = true;
    }

    if (initialLoading) {
      this.addBluePillFilterParameters(true);

      this.setBrandBluePillFilter();

      this.interactiveSmartSummaryService
        .getSmartSummaries(this.dashboard.project_id, this.dashboard.id, this.widget.id)
        .then((res) => {
          this.storedSmartSummaries = res;
          this.isSmartSummaryGenerating = this.storedSmartSummaries.some(
            (sum) => sum.status === 'RUNNING'
          );
          if (this.isSmartSummaryEnabled) {
            this.interactiveSmartSummaryMenu = this.buildSmartSummaryMenu();
            this.removeInteractiveSmartSummaryMenu = this.buildSmartSummaryRemoveMenu();
          }
        });
    }

    this.loadingFinished.emit(initialLoading);
    this.isLoading = false;
  }

  private async refreshOld(
    breakdownCalculationParams: PredictiveChartOptionParams,
    driverCalculationParams: PredictiveChartOptionParams,
    reset = true,
    initialLoading = false,
    refreshedDatasource?: WidgetDataSource,
    isDataSourceChanged = false
  ) {
    const dateFormat = 'YYYY-MM-DD[T]HH:mm:ssZZ';
    const previousDate = this.dateRange.previous;

    const currentDate = this.dateRange.current;

    const timezone = this.timezoneService.timezone;

    const minCurrentDate = moment(currentDate.first).tz(timezone);
    const maxCurrentDate = moment(currentDate.last).tz(timezone);
    const minPreviousDate = moment(previousDate.first).tz(timezone);
    const maxPreviousDate = moment(previousDate.last).tz(timezone);

    const currentBreakdownCalculationParams: PredictiveChartOptionParams = {
      ...breakdownCalculationParams,
    };
    currentBreakdownCalculationParams.minDate = minCurrentDate.startOf('day').format(dateFormat);
    currentBreakdownCalculationParams.maxDate = maxCurrentDate.endOf('day').format(dateFormat);

    const currentDriverCalculationParams: PredictiveChartOptionParams = {
      ...driverCalculationParams,
    };
    currentDriverCalculationParams.minDate = minCurrentDate.startOf('day').format(dateFormat);
    currentDriverCalculationParams.maxDate = maxCurrentDate.endOf('day').format(dateFormat);

    const trendDriverCalculationParams: PredictiveChartOptionParams = {
      ...driverCalculationParams,
    };
    trendDriverCalculationParams.minDate = minPreviousDate.startOf('day').format(dateFormat);
    trendDriverCalculationParams.maxDate = maxPreviousDate.endOf('day').format(dateFormat);

    try {
      const promises: Promise<Calculation>[] = [];

      const currentDateChange =
        this.displayedCurrentDateRange?.first.toDateString() !== currentDate.first.toDateString() ||
        this.displayedCurrentDateRange?.last.toDateString() !== currentDate.last.toDateString();
      const trendDateChange =
        this.displayedTrendDateRange?.first.toDateString() !== previousDate.first.toDateString() ||
        this.displayedTrendDateRange?.last.toDateString() !== previousDate.last.toDateString();

      if (currentDateChange || reset) {
        promises.push(
          this.predictiveService.getOldCalculation(
            this.payload.projectId,
            this.widgetDataSource.collectionName,
            currentBreakdownCalculationParams as unknown as QueryParams
          )
        );
        promises.push(
          this.predictiveService.getOldCalculation(
            this.payload.projectId,
            this.widgetDataSource.collectionName,
            currentDriverCalculationParams as unknown as QueryParams
          )
        );
      }
      if (trendDateChange || reset) {
        promises.push(
          this.predictiveService.getOldCalculation(
            this.payload.projectId,
            this.widgetDataSource.collectionName,
            trendDriverCalculationParams as unknown as QueryParams
          )
        );
      }
      const calculations = await Promise.all(promises);

      if (currentDateChange || reset) {
        this.breakdownCalculation = calculations.shift();
        this.displayedCurrentDateRange = currentDate;
        this.driverCalculation = calculations.shift();
        this.displayedCurrentDateRange = currentDate;
      }
      if (trendDateChange || reset) {
        this.trendCalculation = calculations.shift();
        this.displayedTrendDateRange = previousDate;
      }

      if (!this.widgetGroup.parameters.dependentField) {
        // Backward compatibility for BigNumbers added before DASH-5462
        setTimeout(() => this.dashboardViewComponent.refreshWidgets(this.widgetGroup.widgets));
      }
      //currentData calculation returned, and this.widgetGroup.paramamters. dependentField,
      //breakdownColumn, and driverAnalysisId set (for IPD default widgetGroup)
      this.widgetGroup.parameters.dependentField =
        this.breakdownCalculation.request.dependentColumn;
      this.widgetGroup.parameters.breakdownColumn =
        this.breakdownCalculation.request.breakdownColumn;
      this.widgetGroup.parameters.driverAnalysisId = this.driverCalculation.id;
    } catch (error) {
      if (error.status === 404) {
        this.dataSourceNotFound = true;
      }
      this.loadingFinished.emit(initialLoading);
      this.isLoading = false;
      this.alertService.showErrorToast(
        this.translocoService.translate('widgets.failed-to-get-calculation'),
        error
      );
      throw error;
    }

    await this.accountService.groupFieldsSub.latest();

    this.breakdownTitle =
      FieldService.getFieldLongName(this.breakdownCalculation.request.breakdownColumn) ??
      this.translocoService.translate('widgets.breakdown');

    this.setBreakdowns();
    this.selectedBreakdown = this.allBreakdown;

    this.widgetGroup.parameters.selectedBreakdown = this.selectedBreakdown;

    if (isDataSourceChanged) {
      this.predictiveWidgetDataService.changeTwtraDataSource(refreshedDatasource);

      this.actionPlanIpdService.needActionPlansLoad();
    }

    this.widgetComponent.updateTemplateBooleans();
  }

  private async refreshNew(
    breakdownCalculationParams: PredictiveChartOptionParams,
    driverCalculationParams: PredictiveChartOptionParams,
    _reset = true,
    initialLoading = false,
    refreshedDatasource?: WidgetDataSource,
    isDataSourceChanged = false
  ) {
    try {
      const hasComparisonFilters = !!this.comparison?.filters.length;
      this.canCalculateTrend = hasComparisonFilters && this.comparison.filters.length === 2;

      const calcs = await this.predictiveService.listCalculations(
        this.payload.projectId,
        this.widgetDataSource.collectionName,
        { tags: ['fromWidget'], pageSize: 1024 }
      );
      if (!calcs?.items[0]) {
        return;
      }
      const id = calcs.items.find((c) => c.id === this.widgetDataSource.analysisId)?.id;

      const calcPromises: Promise<Calculation>[] = [];
      const comparisonCalculationParams = Object.assign({}, driverCalculationParams);

      if (hasComparisonFilters) {
        const firstBucketQueryOptions = this.filterService.getWidgetQueryOptions(
          this.widget,
          this.widgetDataSource,
          this.widgetGroup,
          this.comparison.filters[0]
        );

        breakdownCalculationParams.query = this.filterService.getQueryForDocuments(
          firstBucketQueryOptions,
          false,
          true,
          true,
          true
        );
        driverCalculationParams.query = breakdownCalculationParams.query;

        if (this.canCalculateTrend) {
          const secondBucketQueryOptions = this.filterService.getWidgetQueryOptions(
            this.widget,
            this.widgetDataSource,
            this.widgetGroup,
            this.comparison.filters[1]
          );

          comparisonCalculationParams.query = this.filterService.getQueryForDocuments(
            secondBucketQueryOptions,
            false,
            true,
            true,
            true
          );
        }
      }

      //breakdown calc
      calcPromises.push(
        this.predictiveService.getCalculationById(
          this.payload.projectId,
          this.widgetDataSource.collectionName,
          id,
          breakdownCalculationParams as unknown as QueryParams
        )
      );

      //driver calc
      calcPromises.push(
        this.predictiveService.getCalculationById(
          this.payload.projectId,
          this.widgetDataSource.collectionName,
          id,
          driverCalculationParams as unknown as QueryParams
        )
      );

      if (this.canCalculateTrend) {
        calcPromises.push(
          this.predictiveService.getCalculationById(
            this.payload.projectId,
            this.widgetDataSource.collectionName,
            id,
            comparisonCalculationParams as unknown as QueryParams
          )
        );
      }

      const calculations = await Promise.all(calcPromises);
      this.breakdownCalculation = calculations[0];
      this.driverCalculation = calculations[1];
      if (this.canCalculateTrend) {
        this.trendCalculation = calculations[2];
      }
    } catch (error) {
      if (error.status === 404) {
        this.dataSourceNotFound = true;
      }
      this.loadingFinished.emit(initialLoading);
      this.isLoading = false;
      this.alertService.showErrorToast(
        this.translocoService.translate('widgets.failed-to-get-calculation'),
        error
      );
      throw error;
    }

    await this.accountService.groupFieldsSub.latest();

    this.breakdownTitle =
      FieldService.getFieldLongName(this.breakdownCalculation.request.breakdownColumn) ??
      this.translocoService.translate('widgets.breakdown');

    this.setBreakdowns();
    this.selectedBreakdown = this.allBreakdown;

    if (isDataSourceChanged) {
      this.predictiveWidgetDataService.changeTwtraDataSource(refreshedDatasource);

      this.actionPlanIpdService.needActionPlansLoad();

      this.clearSimulation();
    }

    this.widgetComponent.updateTemplateBooleans();
  }

  //On chart options modal close
  public handleChartOptionsChanging(): void {
    this.setScoringMethodInWidgetGroup();

    this.notifyScoringStrategyChange();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.dateRange && !changes.dateRange.firstChange) {
      this.addBluePillFilterParameters();
    }
  }

  public canShowDataTable() {
    return false;
  }

  public isDocumentFilterGroupSelectEnabled(): boolean {
    return false;
  }

  public canAddToWidgetGroup() {
    return false;
  }

  public canAddToComparison() {
    return this.twtraAsAWidget;
  }

  public isWidgetGroupChangeDisabled(): boolean {
    return true;
  }

  public canCreateCollection() {
    return false;
  }

  public get defaultTitle(): string {
    const name =
      this.breakdownCalculation?.request.dependentColumnLabel ||
      this.breakdownCalculation?.request.dependentColumn ||
      '';
    return this.translocoService.translate('widgets.main-metric-name', {
      fieldName: FieldService.getFieldLongName(name),
    });
  }

  private setScoringMethodInWidgetGroup(): void {
    if (!this.twtraAsAWidget) {
      this.widgetGroup.parameters ||= {};

      this.widgetGroup.parameters.dependentRange = this.payload.dependentRange;
      this.widgetGroup.parameters.driversRange = this.payload.driversRange;
      this.widgetGroup.parameters.scoringStrategy = this.payload.driverScoringStrategy;
      this.widgetGroup.parameters.boxDirection = this.payload.driverBoxDirection;
      this.widgetGroup.parameters.boxSize = this.payload.driverBoxSize;
    }
  }

  private notifyScoringStrategyChange(): void {
    this.actionPlanIpdService.changeTwtraScoringStrategy(
      this.payload.driverScoringStrategy ?? Constants.TWTRA_DEFAULT_SCORING_STRATEGY
    );
  }

  private getFilterGroup(): WidgetGroup {
    let group;
    if (this.widgetGroup) {
      group = this.widgetGroup;
    } else {
      for (const key of Object.keys(this.dashboard.widgetGroups)) {
        if (this.dashboard.widgetGroups[key].isImpact) {
          group = this.dashboard.widgetGroups[key];
          break;
        }
      }
    }

    return group;
  }

  private addBluePillFilterParameters(skipWidgetUpdate?) {
    const group = this.getFilterGroup();
    if (this.dateRange && this.dateRange.previous && this.dateRange.current) {
      //Get dates in yyyy-MM-DD format
      const previousStart = this.dateRange.previous.first.toISOString();
      const previousEnd = this.dateRange.previous.last.toISOString();
      const currentStart = this.dateRange.current.first.toISOString();
      const currentEnd = this.dateRange.current.last.toISOString();

      group.parameters = {
        ...group.parameters,
        previousStart,
        previousEnd,
        currentStart,
        currentEnd,
      };

      this.dashboardViewComponent.onUpdateWidgetGroup(
        group,
        skipWidgetUpdate ? [] : undefined,
        undefined,
        true,
        false
      );
    }
  }

  private convertFilterPeriodsToIso() {
    if (
      !this.twtraAsAWidget &&
      this.widgetGroup.parameters.currentStart &&
      !this.widgetGroup.parameters.currentStart.includes('T')
    ) {
      this.widgetGroup.parameters.currentStart = new Date(
        this.widgetGroup.parameters.currentStart
      ).toISOString();
      this.widgetGroup.parameters.currentEnd = new Date(
        this.widgetGroup.parameters.currentEnd
      ).toISOString();
      this.widgetGroup.parameters.previousStart = new Date(
        this.widgetGroup.parameters.previousStart
      ).toISOString();
      this.widgetGroup.parameters.previousEnd = new Date(
        this.widgetGroup.parameters.previousEnd
      ).toISOString();
    }
  }

  private setBrandBluePillFilter() {
    if (this.twtraAsAWidget) {
      return;
    }
    const group = this.getFilterGroup();
    if (this.selectedBreakdown.name === 'All') {
      if (group.parentFilters.metadata.and.length !== 0) {
        this.removeMetadataBluePillFilter(group);
      }
    } else {
      this.addMetadataBluePillFilter(group);
    }
  }

  private addMetadataBluePillFilter(group: WidgetGroup) {
    const filter = {
      analyticItem: `metadata.${this.breakdownCalculation.request.breakdownColumn}`,
      value: this.selectedBreakdown.name,
      parentFilter: true,
    };
    group.parentFilters.metadata.and = [];
    group.parentFilters.metadata.and.push(filter);
    this.dashboardViewComponent.onUpdateWidgetGroup(
      group,
      undefined,
      this.translocoService.translate('widgets.parent-filter-set', { group: group.title }),
      true,
      false
    );
  }

  private removeMetadataBluePillFilter(group: WidgetGroup) {
    group.parentFilters.metadata.and = [];
    this.dashboardViewComponent.onUpdateWidgetGroup(
      group,
      undefined,
      this.translocoService.translate('widgets.parent-filter-removed', { group: group.title }),
      true,
      false
    );
  }

  private getDrivers(breakdown: string): Driver[] {
    const drivers: Driver[] = [];
    for (let i = 0; i < this.driverCalculation.request.driverColumns.length; i++) {
      let resultColumn: ResultColumn;
      let trendResultColumn: ResultColumn;
      if (breakdown === Constants.TWTRA_ALL_BREAKDOWN) {
        resultColumn = this.driverCalculation.result.topline.drivers.find(
          (rc) => rc.label === this.driverCalculation.request.driverColumns[i]
        );

        if (!this.twtraAsAWidget || this.canCalculateTrend) {
          trendResultColumn = this.trendCalculation.result.topline.drivers.find(
            (rc) => rc.label === this.driverCalculation.request.driverColumns[i]
          );
        }
      } else {
        resultColumn = this.driverCalculation.result.breakdown[breakdown].drivers.find(
          (rc) => rc.label === this.driverCalculation.request.driverColumns[i]
        );
        if (!this.twtraAsAWidget || this.canCalculateTrend) {
          trendResultColumn = this.trendCalculation.result.breakdown[breakdown].drivers.find(
            (rc) => rc.label === this.driverCalculation.request.driverColumns[i]
          );
        }
      }

      const driverName = this.driverCalculation.request.driverColumns[i];

      const simulated = this.simulationStorageService.get(breakdown, driverName);

      drivers.push({
        name: driverName,
        importance: resultColumn.importance,
        rescaledRelativeWeights: resultColumn.rescaledRelativeWeights,
        label: this.driverCalculation.request.driverColumnLabels[i],
        current: resultColumn.average,
        trend: trendResultColumn?.average,
        simulated,
        importanceCounter: 0,
        fieldLongName: FieldService.getFieldLongName(driverName),
        importancePopulator: resultColumn.rescaledRelativeWeights ?? resultColumn.importance * 100,
      });
    }

    drivers.sort((a, b) => b.importance - a.importance);

    drivers.forEach((d, i) => (d.importanceCounter = i + 1));

    return drivers;
  }

  private setBreakdowns(): void {
    this.setAllBreakdown();
    this.setSubBreakdowns();

    this.simulate(false);

    const breakdowns = [this.allBreakdown, ...this.subBreakdowns];

    this.breakdowns = breakdowns;
  }

  private setAllBreakdown(): void {
    const allBreakdown = {
      name: Constants.TWTRA_ALL_BREAKDOWN,
      label: Constants.TWTRA_ALL_BREAKDOWN + ' 100%',
      value: this.breakdownCalculation.result.topline.average,
      simulated: null,
      weight: 1,
      count: this.breakdownCalculation.result.topline.count,
      drivers: this.getDrivers(Constants.TWTRA_ALL_BREAKDOWN),
    };

    this.allBreakdown = allBreakdown;
  }

  private setSubBreakdowns(): void {
    let subBreakdowns: Breakdown[] = [];

    for (const breakdown in this.breakdownCalculation.result.breakdown) {
      if (subBreakdowns.some((el) => el.name === breakdown)) {
        continue;
      }

      const weight =
        this.breakdownCalculation.result.breakdown[breakdown].count /
        this.breakdownCalculation.result.topline.count;

      subBreakdowns.push({
        name: breakdown,
        label: breakdown + ` ${Math.round(weight * 100) || 0}%`,
        value: this.breakdownCalculation.result.breakdown[breakdown].average,
        simulated: null,
        weight,
        count: this.breakdownCalculation.result.breakdown[breakdown].count,
        drivers: this.getDrivers(breakdown),
      });
    }

    subBreakdowns = subBreakdowns.sort((a, b) => a.name.localeCompare(b.name));

    this.subBreakdowns = subBreakdowns;
  }

  onSelectBreakdown(breakdown: Breakdown) {
    this.selectedBreakdown = breakdown;

    if (!this.twtraAsAWidget) {
      this.widgetGroup.parameters.selectedBreakdown = this.selectedBreakdown;
    }

    this.setBrandBluePillFilter();
  }

  async onHideBreakdown(breakdown: Breakdown) {
    if (!this.widget.payload.excludedValues) {
      this.widget.payload.excludedValues = [];
    }

    this.widget.payload.excludedValues.push({
      name: breakdown.name,
    });
    this.widget.payload.excludedValues.sort((a, b) =>
      (a.name || a.displayName).localeCompare(b.name || b.displayName)
    );

    if (this.selectedBreakdown === breakdown) {
      this.clearSimulation();
    }
    this.dashboardStore.markDirty();
    this.refresh();
  }

  public driverSelected(driver: Driver) {
    this.payload.selectedDriver = driver.name;
    if (!this.twtraAsAWidget) {
      this.widgetGroup.parameters.selectedDriver = driver;
    }

    this.addBluePillFilterParameters();
  }

  public refreshSmartSummaries() {
    if (this.isSmartSummaryEnabled) {
      this.interactiveSmartSummaryMenu = this.buildSmartSummaryMenu();
      this.removeInteractiveSmartSummaryMenu = this.buildSmartSummaryRemoveMenu();
    }
  }

  public refreshPointSummaries(queryString): void {
    this.pointSummaries = this.getSmartSummariesForPoint(this.selectedBreakdown.name, queryString);
  }

  public onRemoveSmartSummary(): void {
    this.removeSmartSummary();
  }

  public onOpenSmartSummary(queryString) {
    super.openSmartSummary(
      this.selectedBreakdown.name,
      queryString,
      undefined,
      this.driverCalculation.request.properties.analysisId
    );
  }

  public getWidgetQueryOptions(
    dateFilterType: 'previous' | 'current' | 'both'
  ): WidgetQueryOptions {
    const widgetQueryOptions = this.widgetQueryOptions;
    widgetQueryOptions.widgetDataSource = {
      ...widgetQueryOptions.widgetDataSource,
      analysisId: this.analysisId,
    };

    if (this.dateRange) {
      const previousStart = moment(this.dateRange.previous.first);
      const previousEnd = moment(this.dateRange.previous.last);
      const currentStart = moment(this.dateRange.current.first);
      const currentEnd = moment(this.dateRange.current.last);

      widgetQueryOptions.filters.dates = this.filterService.getWidgetDateFilters(
        dateFilterType,
        previousStart,
        previousEnd,
        currentStart,
        currentEnd
      );
    }

    return widgetQueryOptions;
  }

  onOpenSmartSummaryDialog(driver: Driver): void {
    const documentQuery = this.filterService.getQueryForDocuments(
      this.getWidgetQueryOptions('both'),
      false,
      true
    );

    this.dialogService.showDialog(
      SmartSummaryDialogComponent,
      {
        projectId: this.payload.projectId,
        collectionId: this.widgetDataSource.collectionName,
        analysisId: this.analysisId,
        driverAnalysisId: this.driverCalculation.id,
        breakdownColumn: this.driverCalculation.request.breakdownColumn,
        breakdown: this.selectedBreakdown,
        driver,
        dateRange: this.dateRange,
        documentQuery,
      },
      {},
      false
    );
  }

  onSimulate(driver: Driver): void {
    this.simulationStorageService.set(this.selectedBreakdown.name, driver);

    this.simulate();
  }

  onResetBreakdownSimulation(breakdown: Breakdown) {
    this.simulationService.reset(breakdown);

    this.simulationStorageService.removeSimulation(breakdown.name);

    this.simulate();
  }

  onResetDriverSimulation(driver: Driver): void {
    this.simulationStorageService.removeSimulatedValue(this.selectedBreakdown.name, driver.name);

    this.simulate();
  }

  private simulate(shouldDrawChart = true): void {
    this.simulationService.simulate(this.allBreakdown, this.subBreakdowns);

    if (shouldDrawChart) {
      this.chartComponent.drawChart();
    }
  }

  public async exportAsCsv() {
    let csvHeader = this.translocoService.translate('widgets.csv.predictive-header');
    let csvValues = '';
    const driverNames = [];

    for (const driver of this.breakdowns[0].drivers) {
      driverNames.push(driver.name);
      csvHeader += this.translocoService.translate('widgets.csv.predictive-driver-header', {
        fieldName: Utils.escapeCsvString(driver.fieldLongName),
      });
    }

    for (const option of this.breakdowns) {
      csvValues += `\n"${Utils.escapeCsvString(option.name)}",${option.count},${Math.round(
        option.weight * 100
      )},${option.value}`;

      for (const driverName of driverNames) {
        const idx = option.drivers.findIndex((d) => d.name === driverName);
        csvValues += `,${option.drivers[idx].current},${option.drivers[idx].importancePopulator},${option.drivers[idx].simulated}`;
      }
    }

    return { data: csvHeader + csvValues };
  }

  public canApplyComparison(comparison?: Comparison): boolean {
    let res = this.twtraAsAWidget;

    if (comparison) {
      res &&= comparison.filters.length <= 2;
    }

    return res;
  }

  get widgetQueryOptions() {
    return this.filterService.getWidgetQueryOptions(
      this.widget,
      this.widgetDataSource,
      this.widgetGroup,
      this.comparison?.filters[0]
    );
  }

  get analysisId(): string {
    return this.driverCalculation.request.properties.analysisId ?? this.widgetDataSource.analysisId;
  }

  setModalButtonText(p: PredictiveChartOptionParams, type: 'breakdown' | 'driver') {
    let text = '';
    let range: string;
    type === 'breakdown' ? (range = p.dependentRange) : (range = p.driversRange);
    if (p.scoringStrategy === 'BOX') {
      text = `${p.boxDirection} ${p.boxSize} ${p.scoringStrategy} - ${range}`;
    } else {
      p.scoringStrategy ? (text = `${p.scoringStrategy} - ${range}`) : (text = '');
    }
    return text;
  }

  async exportAsPptx(pptx: PptxReport): Promise<void> {
    this.addTitleSlideToPptx(pptx);

    const blob = await this.exportAsImage();

    return this.exportImageToPptx(blob, pptx, this.widgetTitle);
  }

  async exportAsImage(): Promise<Blob> {
    const canvasElements = await this.getCanvasElements();

    const canvasResult = this.drawCanvasResult(canvasElements);

    return firstValueFrom(
      this.httpClient.post<Blob>(canvasResult.dataUrl, canvasResult.body, {
        responseType: 'blob' as 'json',
      })
    );
  }

  private async getCanvasElements(): Promise<HTMLCanvasElement[]> {
    const promises: Promise<HTMLCanvasElement>[] = [];

    switch (this.payload.view) {
      case 'crosstab':
        promises.push(this.crosstabComponent.exportCanvas(), this.chartComponent.exportCanvas());
        break;
      case 'predictor':
      default:
        promises.push(this.breakdownComponent.exportCanvas(), this.chartComponent.exportCanvas());
        break;
    }

    return await Promise.all(promises);
  }

  onMetricSettingsChange(result: MetricSettingsModalResult): void {
    if (this.shouldClearSimulation(this.payload, result)) {
      this.clearSimulation();
    }

    this.payload.mainSettings = result.main;
    this.payload.driverSettings = result.driver;

    this.refresh();
  }

  private shouldClearSimulation(
    payload: WidgetPredictiveOptions,
    result: MetricSettingsModalResult
  ): boolean {
    return (
      this.isMetricScoringChanged(payload.mainSettings, result.main) ||
      this.isMetricScoringChanged(payload.driverSettings, result.driver)
    );
  }

  private isMetricScoringChanged(a: MetricSettings, b: MetricSettings): boolean {
    return a.scoring !== b.scoring;
  }

  private clearSimulation(): void {
    this.simulationService.reset(...this.breakdowns);
    this.simulationStorageService.clear();
  }

  onHoverDriver(driver: Driver | null): void {
    this.hoveredDriver = driver;
  }
}
